import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Send, Check, Info, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ResultsPanel from "@/components/ResultsPanel";
import CurrencyToggle from "@/components/CurrencyToggle";
import LanguageToggle from "@/components/LanguageToggle";
import ThemeToggle from "@/components/ThemeToggle";
import EmailCaptureDialog from "@/components/EmailCaptureDialog";
import AppFooter from "@/components/AppFooter";
import { calculate, createReport } from "@/lib/api";
import { detectLocalCurrency, useCurrencyRates, convertInputs, convertBenchmarkOverrides } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";

const STEPS = [
  { id: "basics",       title: "Campaign basics",       sub: "Who and where" },
  { id: "distribution", title: "Distribution & reach",  sub: "What you'll spend" },
  { id: "impact",       title: "Business impact",       sub: "Traffic and conversion" },
  { id: "measurement",  title: "Measurement",           sub: "Window and market" },
  { id: "results",      title: "Results",               sub: "Scorecard + report" },
];

const INDUSTRIES = [["saas","SaaS"],["ecommerce","E-commerce"],["fintech","Fintech"],["healthcare","Healthcare"],["other","Other"]];
const REGIONS = [["north_america","North America"],["europe","Europe"],["nordic","Nordic (fallback)"],["asia_pacific","Asia Pacific"],["global","Global"]];
const WIRE_TIERS = [["premium","Premium wire"],["standard","Standard"],["budget","Budget"]];
const OBJECTIVES = [["awareness","Awareness"],["leads","Lead generation"],["launch","Product launch"],["funding","Funding announcement"],["other","Other"]];

const DEFAULT = {
  model_type: "b2c",
  currency: "EUR",
  mode: "quick",
  industry: "saas",
  wire_tier: "standard",
  region: "nordic",
  da_range: "30-50",
  credibility: "medium",
  objective: "leads",
  inputs: {
    campaign_name: "",
    baseline_traffic: 50000,
    lift_pct: 20,
    conv_rate: 2.5,
    aov: 250,
    ltv: 900,
    gross_margin: 60,
    timeframe_months: 3,
    risk_reduction_value: 0,
    costs: { writing: 800, agency: 1500, distribution: 750, design: 400, paid_amplification: 1000, tracking: 200 },
  },
  benchmarks: {},
};

// ─────────────── LocalStorage persistence ───────────────
// Persists CAMPAIGN INPUT NUMBERS ONLY (baseline traffic, conversion rate, deal
// size, costs — all user-typed planning values). NO PII, NO auth tokens, NO
// email addresses are ever written. Safe for localStorage per OWASP guidance.
// Bump the version whenever the DEFAULT shape changes so stale drafts don't
// hydrate into a form with new required fields missing.
const STORAGE_KEY = "pressimpact.wizard.draft.v1";
const STORAGE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

function loadDraft() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > STORAGE_MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[wizard] failed to load draft:", err);
    }
    return null;
  }
}

function saveDraft(state, step) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ state, step, savedAt: Date.now() }),
    );
  } catch (err) {
    // Quota exceeded or private-browsing — the draft is a nice-to-have, not critical.
    if (process.env.NODE_ENV !== "production") {
      console.warn("[wizard] failed to save draft:", err);
    }
  }
}

function clearDraft() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[wizard] failed to clear draft:", err);
    }
  }
}

export default function CalculatorWizard() {
  const navigate = useNavigate();
  const { t } = useI18n();
  // Hydrate wizard state + step from localStorage on first render.
  // `initial.hydrated` is true only when a real draft was loaded — never for
  // fresh visitors (fixes: reference-inequality false-positive on DEFAULT).
  const initial = useMemo(() => {
    const draft = loadDraft();
    if (draft?.state && typeof draft.step === "number") {
      return {
        state: draft.state,
        step: Math.max(0, Math.min(STEPS.length - 1, draft.step)),
        hydrated: true,
      };
    }
    return { state: DEFAULT, step: 0, hydrated: false };
  }, []);
  const [step, setStep] = useState(initial.step);
  const [state, setState] = useState(initial.state);
  const [results, setResults] = useState(null);
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // Only true when a real draft was hydrated OR the user has typed. Not
  // triggered by any programmatic setState (e.g. currency auto-detect, reset).
  const [hasUserChanges, setHasUserChanges] = useState(initial.hydrated);
  const { rates, asOf, source, formatDate } = useCurrencyRates();
  const detectedOnce = useRef(false);
  const hydratedOnce = useRef(false);

  // Toast once on mount if we hydrated a real draft.
  // Defer slightly so sonner's Toaster is guaranteed subscribed before we fire.
  useEffect(() => {
    if (initial.hydrated && !hydratedOnce.current) {
      hydratedOnce.current = true;
      const id = setTimeout(() => {
        toast.success("Draft restored", {
          description: "We picked up where you left off. Reset from the header to start over.",
        });
      }, 250);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage — ONLY when the user has actively made changes.
  // This prevents writing DEFAULT on mount (fresh visitor) or after reset.
  useEffect(() => {
    if (!hasUserChanges) return;
    const id = setTimeout(() => saveDraft(state, step), 300);
    return () => clearTimeout(id);
  }, [state, step, hasUserChanges]);

  function resetDraft() {
    clearDraft();
    setState(DEFAULT);
    setStep(0);
    setResults(null);
    setHasUserChanges(false);
    toast.success("Draft cleared — starting fresh");
  }

  useEffect(() => {
    if (detectedOnce.current) return;
    detectedOnce.current = true;
    const d = detectLocalCurrency();
    if (d && d !== state.currency) setState((s) => ({ ...s, currency: d }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payload = useMemo(() => ({
    model_type: state.model_type,
    currency: state.currency,
    mode: state.mode,
    industry: state.industry,
    wire_tier: state.wire_tier,
    region: state.region,
    da_range: state.da_range,
    credibility: state.credibility,
    inputs: state.inputs,
    benchmarks: state.benchmarks,
  }), [state]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await calculate(payload);
        setResults(r.results);
        setFlags(r.benchmark_flags);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [payload]);

  function handleCurrencyChange(next) {
    if (next === state.currency) return;
    const prev = state.currency;
    setState((s) => ({
      ...s,
      currency: next,
      inputs: convertInputs(s.inputs, prev, next, rates),
      benchmarks: convertBenchmarkOverrides(s.benchmarks, prev, next, rates),
    }));
    setHasUserChanges(true);
    toast.success(`Converted ${prev} → ${next}`);
  }

  const upd = (patch) => { setState({ ...state, ...patch }); setHasUserChanges(true); };
  const updIn = (k, v) => { setState({ ...state, inputs: { ...state.inputs, [k]: v } }); setHasUserChanges(true); };
  const updCost = (k, v) => { setState({ ...state, inputs: { ...state.inputs, costs: { ...state.inputs.costs, [k]: v } } }); setHasUserChanges(true); };

  async function performGeneration({ email }) {
    setGenerating(true);
    try {
      const r = await createReport({
        ...payload,
        report_name: state.inputs.campaign_name || "Untitled campaign",
        author_email: email || undefined,
      });
      toast.success("Report generated");
      setEmailDialogOpen(false);
      clearDraft();
      navigate(`/report/${r.slug}`);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Free tier limit reached");
      else toast.error("Could not generate report", { description: e?.message });
    } finally {
      setGenerating(false);
    }
  }

  const isLast = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-[#FFFDF6] text-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 bg-[#FFFDF6]/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.24em] font-semibold">PressImpact</span>
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageToggle />
            <CurrencyToggle value={state.currency} onChange={handleCurrencyChange} rates={rates} asOf={asOf} source={source} formatDate={formatDate} />
            {hasUserChanges && (
              <button
                type="button"
                onClick={resetDraft}
                data-testid="wizard-reset-btn"
                title="Reset the wizard and clear the saved draft"
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:border-slate-400 text-xs uppercase tracking-[0.18em] font-semibold rounded-sm transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            )}
          </div>
        </div>
      </header>

      <EmailCaptureDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} onConfirm={performGeneration} generating={generating} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        {/* Progress */}
        <ol className="grid grid-cols-5 gap-2 mb-8" data-testid="wizard-progress">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <li key={s.id}>
                <button
                  data-testid={`step-${s.id}`}
                  onClick={() => i <= step && setStep(i)}
                  disabled={i > step}
                  className={`w-full text-left transition-opacity ${i > step ? "opacity-40 cursor-not-allowed" : "hover:opacity-90"}`}
                >
                  <div className={`h-1 mb-2 ${active ? "bg-slate-900" : done ? "bg-[#FFC53D]" : "bg-slate-200"}`} />
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] mono tracking-widest ${active ? "text-slate-900" : done ? "text-slate-500" : "text-slate-400"}`}>0{i+1}</span>
                    {done && <Check className="h-3 w-3 text-emerald-600" strokeWidth={3} />}
                  </div>
                  <div className={`text-xs font-semibold mt-1 ${active ? "text-slate-900" : "text-slate-500"} leading-tight`}>
                    {s.title}
                  </div>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: step form */}
          <div className="lg:col-span-7">
            <div className="border border-slate-200 bg-white p-6 sm:p-8" data-testid={`wizard-step-${STEPS[step].id}`}>
              <p className="text-[10px] mono tracking-[0.2em] text-slate-400 mb-1">STEP 0{step+1} / 05 · {STEPS[step].sub.toUpperCase()}</p>
              <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mb-6">{STEPS[step].title}</h2>

              {step === 0 && (
                <div className="space-y-5">
                  <Field label="Campaign name">
                    <Input data-testid="input-campaign-name" value={state.inputs.campaign_name} onChange={(e) => updIn("campaign_name", e.target.value)} placeholder="Q1 product launch" className="rounded-sm border-slate-200" />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Model type">
                      <Tabs value={state.model_type} onValueChange={(v) => upd({ model_type: v })}>
                        <TabsList data-testid="input-model" className="bg-slate-100 rounded-sm">
                          <TabsTrigger value="b2c">B2C</TabsTrigger>
                          <TabsTrigger value="b2b">B2B</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </Field>
                    <Select label="Industry" value={state.industry} onChange={(v) => upd({ industry: v })} options={INDUSTRIES} testid="input-industry" />
                    <Select label="Market / region" value={state.region} onChange={(v) => upd({ region: v })} options={REGIONS} testid="input-region" />
                    <Select label="Campaign objective" value={state.objective} onChange={(v) => upd({ objective: v })} options={OBJECTIVES} testid="input-objective" />
                  </div>
                  <ValuePreview text="Your estimate will combine campaign cost, expected reach, media value, and conversion assumptions." />
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <Select label="Wire tier" value={state.wire_tier} onChange={(v) => upd({ wire_tier: v })} options={WIRE_TIERS} testid="input-wire-tier" />
                  <p className="text-xs text-slate-500 -mt-2">Determines expected impressions, media pickup rate, and expected backlinks — all pre-filled from public benchmarks.</p>

                  <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                    <Label className="col-span-full text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Campaign costs ({state.currency})</Label>
                    {[["writing","Writing"],["agency","Agency / labour"],["distribution","Distribution"],["design","Design"],["paid_amplification","Paid amplification"],["tracking","Tracking / tools"]].map(([k,l]) => (
                      <NumField key={k} label={l} value={state.inputs.costs[k]} onChange={(v) => updCost(k, v)} testid={`cost-${k}`} />
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <NumField label="Baseline monthly traffic" value={state.inputs.baseline_traffic} onChange={(v) => updIn("baseline_traffic", v)} testid="input-baseline-traffic" tip="Your current monthly visits before the campaign." />
                    <NumField label="Expected lift %" value={state.inputs.lift_pct} onChange={(v) => updIn("lift_pct", v)} suffix="%" testid="input-lift-pct" tip="Traffic increase you expect if the story lands. Weighted by pickup rate." />
                    <NumField label="Conversion rate" value={state.inputs.conv_rate} onChange={(v) => updIn("conv_rate", v)} suffix="%" testid="input-conv-rate" tip="Visitor → lead rate on landing pages." />
                    <NumField label={state.model_type === "b2b" ? "Average deal size" : "AOV"} value={state.inputs.aov} onChange={(v) => updIn("aov", v)} testid="input-aov" />
                    {state.model_type === "b2b" && (
                      <NumField label="LTV (optional)" value={state.inputs.ltv} onChange={(v) => updIn("ltv", v)} testid="input-ltv" tip="Leave blank if unknown — we'll assume 3.5× AOV." />
                    )}
                    <NumField label="Gross margin %" value={state.inputs.gross_margin} onChange={(v) => updIn("gross_margin", v)} suffix="%" testid="input-gross-margin" />
                  </div>
                  <p className="text-xs text-slate-500">All figures marked <strong>optional</strong> use benchmark defaults if left blank — labelled as assumptions in the report.</p>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5">
                  <NumField label="Campaign timeframe (months)" value={state.inputs.timeframe_months} onChange={(v) => updIn("timeframe_months", v)} testid="input-timeframe" tip="Window over which benefit accrues." />
                  <div className="border border-slate-100 bg-slate-50 p-4 text-xs text-slate-600 leading-relaxed">
                    <div className="flex items-center gap-2 mb-2 text-slate-700 font-semibold">
                      <Info className="h-3.5 w-3.5" /> Benchmark provenance
                    </div>
                    Currency: <strong>{state.currency}</strong> · ECB rate as of {formatDate(asOf) || "—"}<br />
                    Benchmark market: <strong>{state.region}</strong>{state.region === "nordic" && " · fallback to European average pending Nordic licence"}<br />
                    Wire tier: <strong>{state.wire_tier}</strong>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">
                    Your estimate is live on the right. Every figure is a modelled projection labelled clearly on the report. Generate the boardroom-ready version to share with stakeholders.
                  </p>
                  <Button
                    data-testid="wizard-generate-btn"
                    size="lg"
                    onClick={() => setEmailDialogOpen(true)}
                    disabled={generating}
                    className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-sm shadow-[6px_6px_0_0_#FFC53D] hover:shadow-[4px_4px_0_0_#FFC53D] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>) : (<><Send className="h-4 w-4 mr-2" /> Email me the full report</>)}
                  </Button>
                </div>
              )}

              {/* Nav */}
              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
                <Button
                  data-testid="wizard-back-btn"
                  variant="outline"
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                  className="rounded-sm border-slate-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                {!isLast && (
                  <Button
                    data-testid="wizard-next-btn"
                    onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))}
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  >
                    {step === 3 ? "Review my impact" : "Continue"} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Right: live results — always visible so users see value from step 1 */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <ResultsPanel
                results={results}
                currency={state.currency}
                modelType={state.model_type}
                loading={loading}
                inputs={state.inputs}
                flags={flags}
              />
            </div>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function NumField({ label, value, onChange, suffix, tip, testid }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</Label>
        {tip && <span className="text-[10px] text-slate-400" title={tip}>ⓘ</span>}
      </div>
      <div className="relative">
        <Input data-testid={testid} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="rounded-sm border-slate-200 pr-8" />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 mono">{suffix}</span>}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</Label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]">
        {options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function ValuePreview({ text }) {
  return (
    <div className="mt-2 flex items-start gap-3 border-l-2 border-[#FFC53D] pl-4 py-2 text-sm text-slate-700 leading-relaxed">
      {text}
    </div>
  );
}
